import { useEffect, useRef, useState } from "react";
import { useAnalytics } from "../../lib/analytics";
import { useClientTranslations } from "../../lib/i18n/client";

const LANGUAGES = [
	{ code: "en", name: "English", nativeName: "English", flag: "🇺🇸" },
	{ code: "he", name: "Hebrew", nativeName: "עברית", flag: "🇮🇱" },
	{ code: "es", name: "Spanish", nativeName: "Español", flag: "🇪🇸" },
	{ code: "fr", name: "French", nativeName: "Français", flag: "🇫🇷" },
	{ code: "ru", name: "Russian", nativeName: "Русский", flag: "🇷🇺" },
];

interface LanguageSelectorProps {
	className?: string;
	variant?: "dropdown" | "compact";
	/** Context for analytics (e.g., "onboarding", "settings") */
	context?: string;
}

export function LanguageSelector({
	className = "",
	variant = "compact",
	context,
}: LanguageSelectorProps) {
	const { locale, changeLanguage } = useClientTranslations();
	const analytics = useAnalytics();
	const [isOpen, setIsOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		}

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const currentLanguage =
		LANGUAGES.find((lang) => lang.code === locale) || LANGUAGES[0];

	const handleLanguageSelect = async (langCode: string) => {
		if (isLoading) return;
		analytics.logEvent("language_changed", langCode, {
			previous_language: locale,
			context: context || "unknown",
		});
		// Log specific event for onboarding context
		if (context === "onboarding") {
			analytics.logEvent("onboarding_language_changed", langCode, {
				previous_language: locale,
			});
		}
		setIsOpen(false);
		setIsLoading(true);
		try {
			await changeLanguage(langCode);
		} finally {
			setIsLoading(false);
		}
	};

	if (variant === "compact") {
		return (
			<div
				className={`relative ${className}`}
				ref={dropdownRef}
				style={{ position: "relative", zIndex: 1000 }}
			>
				<button
					type="button"
					onClick={() => {
						analytics.logEvent("language_selector_opened");
						setIsOpen(!isOpen);
					}}
					disabled={isLoading}
					style={{
						display: "flex",
						alignItems: "center",
						gap: "8px",
						padding: "4px 8px",
						fontSize: "14px",
						borderRadius: "6px",
						border: "none",
						background: "transparent",
						cursor: isLoading ? "not-allowed" : "pointer",
						opacity: isLoading ? 0.5 : 1,
						transition: "background-color 0.2s",
					}}
					onMouseEnter={(e) => {
						if (!isLoading)
							(e.target as HTMLElement).style.backgroundColor =
								"rgba(0, 0, 0, 0.05)";
					}}
					onMouseLeave={(e) => {
						(e.target as HTMLElement).style.backgroundColor = "transparent";
					}}
					aria-label={`Current language: ${currentLanguage.name}`}
				>
					<span style={{ fontSize: "20px" }}>{currentLanguage.flag}</span>
					<span style={{ fontWeight: 500 }}>{currentLanguage.nativeName}</span>
					{isLoading ? (
						<div
							style={{
								width: "16px",
								height: "16px",
								border: "2px solid #ddd",
								borderTop: "2px solid #2563eb",
								borderRadius: "50%",
								animation: "spin 1s linear infinite",
							}}
						/>
					) : (
						<svg
							width="16"
							height="16"
							viewBox="0 0 16 16"
							fill="currentColor"
							style={{
								transition: "transform 0.2s",
								transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
							}}
							aria-label="Toggle language dropdown"
						>
							<title>Toggle dropdown</title>
							<path
								d="M4 6l4 4 4-4"
								stroke="currentColor"
								strokeWidth="2"
								fill="none"
							/>
						</svg>
					)}
				</button>

				{isOpen && !isLoading && (
					<div
						style={{
							position: "absolute",
							top: "100%",
							left: 0,
							marginTop: "4px",
							width: "192px",
							backgroundColor: "white",
							borderRadius: "6px",
							boxShadow:
								"0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
							border: "1px solid #e5e7eb",
							padding: "4px 0",
							zIndex: 9999,
						}}
					>
						{LANGUAGES.map((language) => (
							<button
								key={language.code}
								type="button"
								onClick={() => handleLanguageSelect(language.code)}
								style={{
									width: "100%",
									display: "flex",
									alignItems: "center",
									gap: "12px",
									padding: "8px 12px",
									fontSize: "14px",
									textAlign: "left",
									border: "none",
									background:
										language.code === locale ? "#eff6ff" : "transparent",
									color: language.code === locale ? "#1e40af" : "#000",
									cursor: "pointer",
									transition: "background-color 0.2s",
								}}
								onMouseEnter={(e) => {
									if (language.code !== locale)
										(e.target as HTMLElement).style.backgroundColor = "#f9fafb";
								}}
								onMouseLeave={(e) => {
									if (language.code !== locale)
										(e.target as HTMLElement).style.backgroundColor =
											"transparent";
								}}
								dir="ltr"
							>
								<span style={{ fontSize: "20px" }}>{language.flag}</span>
								<span style={{ fontWeight: 500 }}>{language.nativeName}</span>
							</button>
						))}
					</div>
				)}
			</div>
		);
	}

	return null;
}
